import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  signal,
} from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ApiCorretoraService } from 'src/app/services/api-corretora.service';
import { ApiUsuarioService } from 'src/app/services/api-usuario.service';
import { ModulosDeFormulario } from 'src/app/utils/formsModules';
import { FiltroComponent } from '../../filtro.component';

@Component({
  selector: 'sub-filtro-corretora',
  templateUrl: './corretora.component.html',
  styleUrls: ['../../filtro.component.scss'],
  imports: [
    CommonModule,
    ModulosDeFormulario,
    MatRadioModule,
    MatMenuModule,
    MatTooltipModule,
  ],
})
export class CorretoraComponent implements OnInit, OnDestroy {
  @Input() filtroCorretora: string = '';
  @Output() filtroCorretoraChange = new EventEmitter<{ id: string; nomeFan: string }>();

  private readonly LIMIT = 30;
  private readonly MIN_BUSCA = 3;

  private apiCorretora = inject(ApiCorretoraService);
  private apiUsuario = inject(ApiUsuarioService);

  protected matrizId = '';
  protected corretoras = signal<any[]>([]);
  protected carregando = signal<boolean>(false);
  protected erro = signal<boolean>(false);
  protected fimDaLista = signal<boolean>(false);

  private page = 1;
  private busca = '';
  private requestId = 0;
  private buscaInput$ = new Subject<string>();
  private subscription = new Subscription();

  constructor(@Optional() private filtroComp?: FiltroComponent) {}

  ngOnInit(): void {
    this.matrizId = this.apiUsuario.sessao()?.corretoraId ?? '';
    this.subscription.add(
      this.buscaInput$
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((valor) => {
          this.busca = valor;
          this.carregar(true);
        })
    );
    this.carregar(true);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private async carregar(reset: boolean): Promise<void> {
    if (reset) {
      this.page = 1;
      this.fimDaLista.set(false);
      this.corretoras.set([]);
    } else if (this.fimDaLista() || this.carregando()) {
      return;
    }

    const reqId = ++this.requestId;
    this.carregando.set(true);
    this.erro.set(false);

    try {
      const buscaValida =
        this.busca && this.busca.length >= this.MIN_BUSCA
          ? this.busca
          : undefined;

      const ret: any = await this.apiCorretora.corretorasCompanyGroup({
        page: this.page,
        limit: this.LIMIT,
        busca: buscaValida,
        order: 'asc',
      });

      if (reqId !== this.requestId) return;

      const data = ret?.data ?? [];
      this.fimDaLista.set(data.length < this.LIMIT);
      this.corretoras.update((prev) => (reset ? data : [...prev, ...data]));
    } catch (error) {
      if (reqId !== this.requestId) return;
      console.error('Erro ao buscar corretoras do filtro:', error);
      this.erro.set(true);
    } finally {
      if (reqId === this.requestId) this.carregando.set(false);
    }
  }

  onScroll(event: Event): void {
    const el = event.target as HTMLElement;
    if (this.carregando() || this.fimDaLista()) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      this.page++;
      this.carregar(false);
    }
  }

  onBuscaChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.buscaInput$.next(input.value.toLowerCase().trim());
  }

  onFiltroChange(corretoraId: string) {
    if (!corretoraId || corretoraId === this.filtroCorretora) return;
    this.filtroCorretora = corretoraId;
    const corretora = this.corretoras().find((c) => c.id === corretoraId);
    this.filtroCorretoraChange.emit({
      id: corretoraId,
      nomeFan: corretora?.nomeFan ?? '',
    });
    this.filtroComp?.sendFilter(false);
  }
}
